// umlaut-check: skip — "zuruecksetzen" ist der technische Name aus
// useGemerkteFilter.jsx, bleibt ASCII.
import React, {useEffect, useRef, useState} from 'react';
import InertiaTable from 'laravel-inertia-react-table';
import {Link, usePage} from '@inertiajs/react';
import {Alert, Button, Card, Container, Modal, ProgressBar} from 'react-bootstrap';
import axios from 'axios';
import {FlexToast, useExternalControls, usePolling} from 'laravel-inertia-backend';
import TimestampTableCell from '../../Components/TimestampTableCell.jsx';
import StatusBadgeTableCell from '../../Components/StatusBadgeTableCell.jsx';
import {makeChangesCountLinkTableCell} from '../../Components/ChangesCountLinkTableCell.jsx';
import HelpTooltip from '../../Components/HelpTooltip.jsx';
import {AlleFilterZuruecksetzenLink, useGemerkteServerFilter} from "../../Components/Filter/useGemerkteFilter.jsx";

const ChangesCountLinkTableCell = makeChangesCountLinkTableCell('/stock/b2c-availability/runs');

// Intervall für das Nachladen des laufenden/letzten Runs (Fortschrittsanzeige).
const STATUS_POLL_INTERVAL_MS = 2500;

function isActiveRun(run) {
    return !!run && (run.status === 'pending' || run.status === 'processing');
}

export default function B2cAvailability() {
    const {b2c_stock_sync_runs, col_definitions, stock_sync_settings, active_run, auth} = usePage().props;
    usePolling('b2c_stock_sync_runs');
    const {weichtAb, zuruecksetzen} = useGemerkteServerFilter("stock.b2c-availability", {benutzerId: auth?.id});
    const {addToast} = useExternalControls();

    const [run, setRun] = useState(active_run ?? null);
    const [triggering, setTriggering] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const pollRef = useRef(null);

    // Beim serverseitigen Neuladen der Seite (z. B. durch usePolling) den
    // aktuellen Stand aus den Props übernehmen.
    useEffect(() => {
        setRun(active_run ?? null);
    }, [active_run]);

    // Solange ein Run läuft, alle STATUS_POLL_INTERVAL_MS den Status-Endpunkt
    // abfragen - für ALLE Besucher der Seite, nicht nur den Auslöser.
    useEffect(() => {
        if (isActiveRun(run) && !pollRef.current) {
            pollRef.current = setInterval(async () => {
                try {
                    const {data} = await axios.get('/stock/b2c/status');
                    setRun(data.run);
                    if (!isActiveRun(data.run) && pollRef.current) {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                    }
                } catch (e) {
                    // Einzelner Polling-Fehler wird ignoriert, der nächste Tick versucht es erneut.
                }
            }, STATUS_POLL_INTERVAL_MS);
        }

        if (!isActiveRun(run) && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }

        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [run]);

    const triggerSync = async () => {
        setTriggering(true);
        try {
            const {data} = await axios.post('/stock/b2c/trigger');
            setRun({
                id: data.runId,
                status: data.status,
                total: 0,
                processed: 0,
                progress_percent: 0,
                phase: null,
                changes_count: 0,
                articles_checked: 0,
            });
            addToast(<FlexToast title={'B2C-Lagerabgleich'} autohide={true} message={'Abgleich gestartet.'}/>);
        } catch (e) {
            if (e.response?.status === 409) {
                const respRun = e.response.data;
                addToast(<FlexToast variant={'warning'} title={'B2C-Lagerabgleich'} autohide={true}
                                    message={respRun?.message || 'Es läuft bereits ein Abgleich.'}/>);
                setRun((prev) => prev ?? {
                    id: respRun?.runId,
                    status: respRun?.status || 'processing',
                    total: 0,
                    processed: 0,
                    progress_percent: 0,
                    phase: null,
                    changes_count: 0,
                    articles_checked: 0,
                });
            } else {
                const msg = e.response?.data?.message || e.message;
                addToast(<FlexToast variant={'danger'} title={'B2C-Lagerabgleich'} autohide={false}
                                    message={`Start fehlgeschlagen: ${msg}`}/>);
            }
        } finally {
            setTriggering(false);
            setShowConfirm(false);
        }
    };

    const enabled = stock_sync_settings?.enabled ?? false;
    const mailRecipients = stock_sync_settings?.mail_recipients || 'nicht hinterlegt';
    const running = isActiveRun(run);

    return (
        <>
            <Container fluid className="pt-2">
                <div className="mt-1 mb-3 d-flex">
                    <h1>B2C-Bestandsaktualisierung</h1>
                </div>

                <Card className="mb-4">
                    <Card.Body>
                        <Card.Title>Wie funktioniert der Abgleich?</Card.Title>
                        <Card.Text className="mb-3">
                            Der Abgleich überträgt den Lagerbestand aus Weclapp (Lager SNV Weilheim) als
                            Stückzahl in den B2C-Shop (WooCommerce). Anders als beim B2B-Abgleich wird nicht
                            nur „Auf Lager"/„In Zulauf" umgeschaltet, sondern die konkrete Menge
                            geschrieben – WooCommerce leitet den Bestandsstatus daraus selbst ab.
                            <br/>
                            <em>
                                Unlike the B2B sync, this process writes the actual stock quantity to
                                WooCommerce instead of a simple in-stock/out-of-stock flag.
                            </em>
                        </Card.Text>
                        <Card.Text as="div">
                            <ul className="mb-2 ps-3">
                                <li>
                                    <strong>Datenquelle:</strong> Weclapp (Mandant Feuerstein), Lager SNV Weilheim.
                                </li>
                                <li>
                                    <strong>Ziel:</strong> WooCommerce-Feld <code>stock_quantity</code>, je Artikel
                                    in beiden Sprach-Varianten (DE + EN, wegen WPML).
                                </li>
                                <li>
                                    <strong>Pack-Berechnung:</strong> Pack-Artikel (Sets/Dosen-Packs ohne eigenen
                                    SNV-Bestand) erhalten als Zielmenge den kleinsten Bestand ihrer
                                    Komponenten-Artikel, geteilt durch die Packgröße (abgerundet).
                                    <br/>
                                    <em>
                                        Pack stock = smallest component stock divided by pack size (rounded down).
                                    </em>
                                </li>
                                <li>
                                    <strong>Turnus:</strong> täglich, automatisch über den Scheduler – zusätzlich
                                    jederzeit manuell anstoßbar.
                                </li>
                            </ul>
                        </Card.Text>
                        <Alert variant={enabled ? 'success' : 'warning'} className="d-flex justify-content-between align-items-center mb-3">
                            <span>
                                Automatischer Abgleich ist aktuell <strong>{enabled ? 'aktiviert' : 'deaktiviert'}</strong>.
                                {' '}Report-Mail an {mailRecipients}.
                            </span>
                            <Link href="/settings" className="btn btn-outline-secondary btn-sm ms-3">
                                Einstellungen ändern
                            </Link>
                        </Alert>

                        <Button variant="primary" onClick={() => setShowConfirm(true)} disabled={triggering || running}>
                            {running ? 'Abgleich läuft…' : 'Jetzt Bestände abgleichen'}
                        </Button>

                        {run && (
                            <div className="mt-3">
                                {running ? (
                                    <>
                                        <div className="mb-1 text-muted">
                                            {run.phase || 'Wird vorbereitet…'}
                                            {run.total > 0 && (
                                                (run.phase || '').toLowerCase().includes('schreiben')
                                                    ? ` · ${run.processed} / ${run.total} Mengen geschrieben`
                                                    : ` · ${run.processed} / ${run.total} Artikel geprüft`
                                            )}
                                        </div>
                                        <ProgressBar
                                            now={run.progress_percent ?? 0}
                                            label={`${run.progress_percent ?? 0}%`}
                                            animated
                                        />
                                    </>
                                ) : run.status === 'completed' ? (
                                    <Alert variant="success" className="mb-0">
                                        Fertig: <strong>{run.changes_count}</strong> von {run.articles_checked} geprüften
                                        Artikeln geändert.
                                        {run.error_message && ` (Hinweis: ${run.error_message})`}
                                    </Alert>
                                ) : run.status === 'failed' ? (
                                    <Alert variant="danger" className="mb-0">
                                        Letzter Abgleich ist fehlgeschlagen.
                                        {run.error_message && ` (Hinweis: ${run.error_message})`}
                                    </Alert>
                                ) : null}
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Container>

            <Modal show={showConfirm} onHide={() => !triggering && setShowConfirm(false)} centered>
                <Modal.Header closeButton={!triggering}>
                    <Modal.Title>Bestandsabgleich wirklich starten?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Dieser Abgleich schreibt die aktuellen SNV-Lagermengen aus Weclapp in den
                        LIVE-B2C-Onlineshop (shop.molotow.com) – je Artikel in DE und EN. Es werden
                        nur Artikel mit abweichender Menge geändert.
                    </p>
                    <p className="mb-0">
                        Das kann einige Minuten dauern (bei einem großen Abgleich ca. 5-10 Minuten).
                        Der Fortschritt wird hier angezeigt; du kannst die Seite in der Zeit offen
                        lassen.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirm(false)} disabled={triggering}>
                        Abbrechen
                    </Button>
                    <Button variant="primary" onClick={triggerSync} disabled={triggering}>
                        {triggering ? 'Wird gestartet…' : 'Ja, Abgleich starten'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <InertiaTable
                key={'b2c-stock-sync-runs-table'}
                data={b2c_stock_sync_runs}
                title={
                    <span className="d-inline-flex align-items-center gap-2">
                        Live-Log
                        <HelpTooltip placement="right" size={0.95}>
                            <div className="text-start">
                                Protokoll der bisherigen Läufe. Aktualisiert sich automatisch, sobald ein
                                neuer Lauf abgeschlossen ist.
                            </div>
                        </HelpTooltip>
                    </span>
                }
                colDefinitions={[...col_definitions]}
                customCellRendererMap={{
                    'started_at': TimestampTableCell,
                    'status': StatusBadgeTableCell,
                    'changes_count': ChangesCountLinkTableCell,
                }}
                customToolbarButtons={<AlleFilterZuruecksetzenLink sichtbar={weichtAb} onClick={zuruecksetzen} />}
            />
        </>
    );
}
